"use client";

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from "react";
import React, { createContext, useState, useEffect, useContext, useCallback, useMemo, useRef } from "react";
import { MyStreamWaveTab } from "@/types/waves.types";
import useLocalPreference from "@/hooks/useLocalPreference";
export enum WaveVotingState {
  NOT_STARTED = "NOT_STARTED",
  ONGOING = "ONGOING",
  ENDED = "ENDED",
}

// Define a type for the updateAvailableTabs parameters
type WaveTabParams = {
  waveId: string | null;
  isChatWave: boolean;
  isMemesWave: boolean;
  votingState: WaveVotingState;
  hasFirstDecisionPassed: boolean;
};
interface ContentTabContextType {
  activeContentTab: MyStreamWaveTab;
  setActiveContentTab: (tab: MyStreamWaveTab) => void;
  availableTabs: MyStreamWaveTab[];
  updateAvailableTabs: (params: WaveTabParams | null) => void;
}
const MEMES_WAVE_LAST_TAB_STORAGE_KEY = "memes_wave_last_tab_by_id";
const isValidWaveTab = (value: unknown): value is MyStreamWaveTab => Object.values(MyStreamWaveTab).includes(value as MyStreamWaveTab);
const isValidWaveTabMap = (value: unknown): value is Record<string, MyStreamWaveTab> => {
  if (value === null || value === undefined || typeof value !== "object") {
    return false;
  }
  return Object.values(value).every(tab => isValidWaveTab(tab));
};
const buildMemesTabs = (votingState: WaveVotingState, hasFirstDecisionPassed: boolean) => {
  const tabs: MyStreamWaveTab[] = [];
  if (votingState !== WaveVotingState.ENDED) {
    tabs.push(MyStreamWaveTab.LEADERBOARD);
  }
  tabs.push(MyStreamWaveTab.CHAT);
  if (hasFirstDecisionPassed) {
    tabs.push(MyStreamWaveTab.WINNERS);
  }
  tabs.push(MyStreamWaveTab.MY_VOTES);
  tabs.push(MyStreamWaveTab.OUTCOME);
  tabs.push(MyStreamWaveTab.FAQ);
  return tabs;
};
const buildDefaultTabs = (votingState: WaveVotingState, hasFirstDecisionPassed: boolean) => {
  const tabs: MyStreamWaveTab[] = [MyStreamWaveTab.CHAT];
  if (votingState !== WaveVotingState.ENDED) {
    tabs.push(MyStreamWaveTab.LEADERBOARD);
  }
  if (hasFirstDecisionPassed) {
    tabs.push(MyStreamWaveTab.WINNERS);
  }
  tabs.push(MyStreamWaveTab.OUTCOME);
  tabs.push(MyStreamWaveTab.MY_VOTES);
  return tabs;
};

// Create the context with a default value
const ContentTabContext = createContext<ContentTabContextType>({
  activeContentTab: MyStreamWaveTab.CHAT,
  setActiveContentTab: () => {},
  availableTabs: [MyStreamWaveTab.CHAT],
  updateAvailableTabs: () => {}
});

// Export a provider component
export const ContentTabProvider: React.FC<{
  children: ReactNode;
}> = t0 => {
  const $ = _c(20);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [tabsByWaveId, setTabsByWaveId] = useLocalPreference(MEMES_WAVE_LAST_TAB_STORAGE_KEY, t1, isValidWaveTabMap);
  const [activeContentTabRaw, setActiveContentTabRaw] = useState(MyStreamWaveTab.CHAT);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [MyStreamWaveTab.CHAT];
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [availableTabs, setAvailableTabs] = useState(t2);
  const currentWaveIdRef = useRef(null);
  const activeContentTabRef = useRef(activeContentTabRaw);
  const tabsByWaveIdRef = useRef(tabsByWaveId);
  let t3;
  let t4;
  if ($[2] !== activeContentTabRaw) {
    t3 = () => {
      activeContentTabRef.current = activeContentTabRaw;
    };
    t4 = [activeContentTabRaw];
    $[2] = activeContentTabRaw;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[5] !== tabsByWaveId) {
    t5 = () => {
      tabsByWaveIdRef.current = tabsByWaveId;
    };
    t6 = [tabsByWaveId];
    $[5] = tabsByWaveId;
    $[6] = t5;
    $[7] = t6;
  } else {
    t5 = $[6];
    t6 = $[7];
  }
  useEffect(t5, t6);
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = tab => {
      setActiveContentTabRaw(tab);
      activeContentTabRef.current = tab;
    };
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const setActiveTabInternal = t7;
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = params => {
      if (!params) {
        setAvailableTabs([MyStreamWaveTab.CHAT]);
        currentWaveIdRef.current = null;
        setActiveTabInternal(MyStreamWaveTab.CHAT);
        return;
      }
      const {
        waveId,
        isChatWave,
        isMemesWave,
        votingState,
        hasFirstDecisionPassed
      } = params;
      let tabs;
      if (isChatWave) {
        tabs = [MyStreamWaveTab.CHAT];
      } else {
        if (isMemesWave) {
          tabs = buildMemesTabs(votingState, hasFirstDecisionPassed);
        } else {
          tabs = buildDefaultTabs(votingState, hasFirstDecisionPassed);
        }
      }
      setAvailableTabs(tabs);
      currentWaveIdRef.current = waveId ?? null;
      const storedTab = waveId ? tabsByWaveIdRef.current[waveId] : undefined;
      const defaultTab = isMemesWave && tabs.includes(MyStreamWaveTab.LEADERBOARD) ? MyStreamWaveTab.LEADERBOARD : MyStreamWaveTab.CHAT;
      const nextTab = storedTab !== undefined && tabs.includes(storedTab) ? storedTab : defaultTab;
      setActiveTabInternal(nextTab);
    };
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const updateAvailableTabs = t8;
  let t9;
  if ($[10] !== availableTabs || $[11] !== setTabsByWaveId) {
    t9 = tab_0 => {
      if (availableTabs.includes(tab_0)) {
        setActiveTabInternal(tab_0);
        const waveId_0 = currentWaveIdRef.current;
        if (waveId_0) {
          const nextMap = {
            ...tabsByWaveIdRef.current,
            [waveId_0]: tab_0
          };
          tabsByWaveIdRef.current = nextMap;
          setTabsByWaveId(nextMap);
        }
      } else {
        setActiveTabInternal(MyStreamWaveTab.CHAT);
      }
    };
    $[10] = availableTabs;
    $[11] = setTabsByWaveId;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const setActiveContentTab = t9;
  let t10;
  if ($[13] !== activeContentTabRaw || $[14] !== availableTabs || $[15] !== setActiveContentTab) {
    t10 = {
      activeContentTab: activeContentTabRaw,
      setActiveContentTab,
      availableTabs,
      updateAvailableTabs
    };
    $[13] = activeContentTabRaw;
    $[14] = availableTabs;
    $[15] = setActiveContentTab;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const contextValue = t10;
  let t11;
  if ($[17] !== children || $[18] !== contextValue) {
    t11 = <ContentTabContext.Provider value={contextValue}>{children}</ContentTabContext.Provider>;
    $[17] = children;
    $[18] = contextValue;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  return t11;
};

// Export a hook for using the context
export const useContentTab = () => {
  return useContext(ContentTabContext);
};